import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  BarChart as BarChartIcon,
  Compare as CompareIcon,
  TrackChanges as TargetIcon,
  Work as WorkIcon,
  People as PeopleIcon,
  Psychology as BrainIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import useStore from '../config/store';

const HomeLayout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, authToken, language, project, organization, showAlert, plan } = useStore();

  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState({
    totalPositions: 0,
    totalApplicants: 0,
    totalCapabilities: 0,
    totalReports: 0,
    totalEvaluations: 0,
    totalComparisons: 0
  });
  const [configExpanded, setConfigExpanded] = useState(false);
  const [error, setError] = useState(null);

  const isAdmin = ["admin", "admin-grafton", "super-admin"].includes(user?.rol);
  const userName = user?.name || user?.displayName || t('topBar.user');

  useEffect(() => {
    if (project) {
      loadMetrics();
    }
  }, [project]);

  const loadMetrics = async () => {
    if (!authToken || !project) return;

    setLoading(true);
    setError(null);

    try {
      // Ejecutar todas las consultas en paralelo usando el mismo patrón que DashboardLayout
      const [
        applicantsResponse,
        positionsResponse,
        evaluationsResponse,
        reportsResponse,
        capabilitiesResponse,
        comparisonsResponse
      ] = await Promise.all([
        // Postulantes del proyecto
        api.get(`/applicants`, {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        }),

        // Posiciones del proyecto
        api.get(`/positions`, {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        }),

        // Evaluaciones del proyecto
        api.get(`/evaluations`, {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        }),

        // Reportes del proyecto
        api.get(`/reports`, {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        }),

        // Capabilities (no necesita projectId)
        api.get(`/capabilities`, {
          params: { language: language },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        }),

        // Comparaciones del proyecto - API específica
        api.get(`/comparisons/by-project`, {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }).catch(err => {
          return { data: { errorCode: 1, response: { data: [] } } };
        })
      ]);

      // Función helper para parsear respuestas de manera robusta (igual que DashboardLayout)
      const parseResponse = (response, apiName) => {
        // Caso 1: errorCode === 0 y data en response.data
        if (response.data?.errorCode === 0 && response.data?.response?.data) {
          const data = response.data.response.data;

          // Si data es un array, devolverlo directamente
          if (Array.isArray(data)) {
            return data;
          }

          // Si data es un objeto único, convertirlo a array
          if (typeof data === 'object' && data !== null) {
            return [data];
          }
        }

        // Caso 2: data directamente en response.data.data
        if (response.data?.data && Array.isArray(response.data.data)) {
          return response.data.data;
        }

        // Caso 3: resultCode 200 y data en response.data
        if (response.data?.resultCode === 200 && response.data?.response?.data) {
          const data = response.data.response.data;
          return Array.isArray(data) ? data : [data];
        }

        return [];
      };

      // Parsear datos correctamente
      const applicants = parseResponse(applicantsResponse, 'Applicants');
      const positions = parseResponse(positionsResponse, 'Positions');
      const evaluations = parseResponse(evaluationsResponse, 'Evaluations');
      const reports = parseResponse(reportsResponse, 'Reports');
      const capabilities = parseResponse(capabilitiesResponse, 'Capabilities');
      const comparisons = parseResponse(comparisonsResponse, 'Comparisons');

      // Asegurar que todos los parámetros sean arrays
      const safeApplicants = Array.isArray(applicants) ? applicants : [];
      const safePositions = Array.isArray(positions) ? positions : [];
      const safeEvaluations = Array.isArray(evaluations) ? evaluations : [];
      const safeReports = Array.isArray(reports) ? reports : [];
      const safeCapabilities = Array.isArray(capabilities) ? capabilities : [];
      const safeComparisons = Array.isArray(comparisons) ? comparisons : [];

      setMetrics({
        totalApplicants: safeApplicants.length,
        totalPositions: safePositions.length,
        totalEvaluations: safeEvaluations.length,
        totalReports: safeReports.length,
        totalCapabilities: safeCapabilities.length,
        totalComparisons: safeComparisons.length
      });

    } catch (err) {
      console.error('Error loading metrics:', err);
      setError(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  // Verificar prerequisitos para cada funcionalidad
  const canCreateReports = metrics.totalPositions > 0;
  const canCreateAssessments = metrics.totalPositions > 0 && metrics.totalApplicants > 0 && metrics.totalCapabilities > 0;
  const canCompareReports = metrics.totalReports >= 2;
  const isTrial = plan === 'trial';
  const cardTones = [
    { cardBg: '#EEF2FF', border: '#C7D2FE', accent: '#6A5AF9', accentDark: '#5A4AE8', buttonColor: 'primary' },
    { cardBg: '#F3F4FF', border: '#C4B5FD', accent: '#7D73EE', accentDark: '#6D63DE', buttonColor: 'secondary' },
    { cardBg: '#F8EEFB', border: '#E9D5FF', accent: '#9754AE', accentDark: '#85479A', buttonColor: 'tertiary' },
    { cardBg: '#EEF2FF', border: '#BFDBFE', accent: '#4F46E5', accentDark: '#4338CA', buttonColor: 'quaternary' },
  ];

  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {t('dashboard.selectProject')}
        </Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={loadMetrics}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t('home.retrying') : t('home.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header de bienvenida */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 1, fontWeight: 600 }}>
          {t('home.welcome', { name: userName })} 👋
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('home.subtitle')}
        </Typography>
        {/* Mostrar plan actual */}
        <Box sx={{ mt: 2 }}>
          <Chip
            label={`Plan: ${plan || 'free'}`}
            variant="outlined"
            sx={{
              textTransform: 'capitalize',
              fontWeight: 500
            }}
          />
        </Box>
      </Box>

      {/* Banner informativo para plan trial */}
      {isTrial && (
        <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('home.trialBanner.title')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('home.trialBanner.description')}
          </Typography>
          <Typography variant="body2">
            {t('home.trialBanner.upgrade')}{' '}
            <a
              href="mailto:david@amandahr.ai"
              style={{ color: 'inherit', fontWeight: 600 }}
            >
              david@amandahr.ai
            </a>
          </Typography>
        </Alert>
      )}

      {/* Configuración Esencial */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{
            width: 4,
            height: 40,
            backgroundColor: '#D97706',
            borderRadius: 1,
            mr: 2
          }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('home.essentialConfig')}
          </Typography>
          <Chip
            label={t('home.prerequisite')}
            size="small"
            sx={{ ml: 2, backgroundColor: '#FEF3C7', color: '#92400E' }}
          />
        </Box>

        {/* Alerta cuando no hay cargos */}
        {metrics.totalPositions === 0 && !loading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('home.noPositionsAlert')}
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Gestión de Cargos */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              backgroundColor: cardTones[0].cardBg,
              border: `1px solid ${cardTones[0].border}`,
              borderRadius: 2,
              height: '100%'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ backgroundColor: cardTones[0].accent, mr: 2, width: 48, height: 48 }}>
                    <TargetIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('home.positionManagement')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {metrics.totalPositions > 0
                        ? t('home.managePositionDesc')
                        : t('home.createPositionDesc')
                      }
                    </Typography>
                  </Box>
                  {/* Mostrar cantidad actual de cargos */}
                  <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: cardTones[0].accent }}>
                      {loading ? <CircularProgress size={24} sx={{ color: cardTones[0].accent }} /> : metrics.totalPositions}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('home.currentPositions')}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => handleNavigate('/positions')}
                  disabled={!isAdmin}
                  sx={{ mb: 2 }}
                >
                  {t('home.managePositions')}
                </Button>

                {!isAdmin && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('home.adminOnlyPositions')}
                  </Alert>
                )}

                {/* Solo mostrar el mensaje de requerimiento cuando NO hay cargos */}
                {metrics.totalPositions === 0 && !loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <WarningIcon sx={{ color: cardTones[0].accent, mr: 1, fontSize: 16 }} />
                    <Typography variant="body2" sx={{ color: cardTones[0].accentDark }}>
                      {t('home.requiredForServices')}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Gestión de Candidatos */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              backgroundColor: cardTones[1].cardBg,
              border: `1px solid ${cardTones[1].border}`,
              borderRadius: 2,
              height: '100%'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ backgroundColor: cardTones[1].accent, mr: 2, width: 48, height: 48 }}>
                    <PeopleIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('home.candidateManagement')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {metrics.totalApplicants > 0
                        ? t('home.manageCandidateDesc')
                        : t('home.createCandidateDesc')
                      }
                    </Typography>
                  </Box>
                  {/* Mostrar cantidad actual de candidatos */}
                  <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: cardTones[1].accent }}>
                      {loading ? <CircularProgress size={24} sx={{ color: cardTones[1].accent }} /> : metrics.totalApplicants}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('home.currentApplicants')}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => handleNavigate('/applicants')}
                  sx={{ mb: 2 }}
                >
                  {t('home.manageCandidates')}
                </Button>

                {/* Solo mostrar el mensaje de requerimiento cuando NO hay candidatos */}
                {metrics.totalApplicants === 0 && !loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <WarningIcon sx={{ color: cardTones[1].accent, mr: 1, fontSize: 16 }} />
                    <Typography variant="body2" sx={{ color: cardTones[1].accentDark }}>
                      {t('home.requiredForAssessments')}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Gestión de Competencias - Solo mostrar si NO es plan free NI trial */}
          {plan !== 'free' && !isTrial && (
            <Grid item xs={12} md={6}>
              <Card sx={{
                backgroundColor: cardTones[2].cardBg,
                border: `1px solid ${cardTones[2].border}`,
                borderRadius: 2,
                height: '100%'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ backgroundColor: cardTones[2].accent, mr: 2, width: 48, height: 48 }}>
                      <BrainIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('home.availableCapabilities')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('home.viewCapabilityDesc')}
                      </Typography>
                    </Box>
                    {/* Mostrar cantidad actual de competencias */}
                    <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                      <Typography variant="h4" sx={{ fontWeight: 600, color: cardTones[2].accent }}>
                        {loading ? <CircularProgress size={24} sx={{ color: cardTones[2].accent }} /> : metrics.totalCapabilities}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('home.currentCapabilities')}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    color="tertiary"
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleNavigate('/capabilities')}
                    sx={{ mb: 2 }}
                  >
                    {t('home.viewCapabilities')}
                  </Button>

                  {/* Solo mostrar el mensaje de requerimiento cuando NO hay competencias */}
                  {metrics.totalCapabilities === 0 && !loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <WarningIcon sx={{ color: cardTones[2].accent, mr: 1, fontSize: 16 }} />
                      <Typography variant="body2" sx={{ color: cardTones[2].accentDark }}>
                        {t('home.requiredForAssessments')}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Servicios Principales */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{
            width: 4,
            height: 40,
            backgroundColor: '#2563EB',
            borderRadius: 1,
            mr: 2
          }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('home.mainServices')}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Evaluación Psicolaboral - Siempre mostrar */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              height: '100%',
              backgroundColor: canCreateReports ? cardTones[0].cardBg : '#F3F4F6',
              border: canCreateReports ? `1px solid ${cardTones[0].border}` : '1px solid #E5E7EB',
              borderRadius: 2
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{
                    backgroundColor: canCreateReports ? cardTones[0].accent : '#9CA3AF',
                    mr: 2,
                    width: 48,
                    height: 48
                  }}>
                    <AddIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('home.integratedReport')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('home.integratedReportDesc')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2, flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {t('home.prerequisites')}:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {metrics.totalPositions > 0 ? (
                      <CheckCircleIcon sx={{ color: '#059669', mr: 1, fontSize: 16 }} />
                    ) : (
                      <WarningIcon sx={{ color: '#DC2626', mr: 1, fontSize: 16 }} />
                    )}
                    <Typography variant="body2">
                      {t('home.positionsCreated')} ({metrics.totalPositions})
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {t('home.candidateAutoCreated')}
                  </Typography>
                </Box>

                <Button
                  variant={canCreateReports ? "contained" : "outlined"}
                  color={canCreateReports ? cardTones[0].buttonColor : 'primary'}
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => handleNavigate('/reports')}
                  disabled={!canCreateReports}
                >
                  {t('home.createReport')}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Assessment Center - Solo mostrar si NO es plan free NI trial */}
          {plan !== 'free' && !isTrial && (
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                backgroundColor: canCreateAssessments ? cardTones[1].cardBg : '#F3F4F6',
                border: canCreateAssessments ? `1px solid ${cardTones[1].border}` : '1px solid #E5E7EB',
                borderRadius: 2
              }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{
                      backgroundColor: canCreateAssessments ? cardTones[1].accent : '#9CA3AF',
                      mr: 2,
                      width: 48,
                      height: 48
                    }}>
                      <AssignmentIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('home.assessmentCenter')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('home.assessmentCenterDesc')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2, flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t('home.prerequisites')}:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {metrics.totalPositions > 0 ? (
                        <CheckCircleIcon sx={{ color: '#059669', mr: 1, fontSize: 16 }} />
                      ) : (
                        <WarningIcon sx={{ color: '#DC2626', mr: 1, fontSize: 16 }} />
                      )}
                      <Typography variant="body2">
                        {t('home.positionsCreated')} ({metrics.totalPositions})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {metrics.totalApplicants > 0 ? (
                        <CheckCircleIcon sx={{ color: '#059669', mr: 1, fontSize: 16 }} />
                      ) : (
                        <WarningIcon sx={{ color: '#DC2626', mr: 1, fontSize: 16 }} />
                      )}
                      <Typography variant="body2">
                        {t('home.applicantsRegistered')} ({metrics.totalApplicants})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {metrics.totalCapabilities > 0 ? (
                        <CheckCircleIcon sx={{ color: '#059669', mr: 1, fontSize: 16 }} />
                      ) : (
                        <WarningIcon sx={{ color: '#DC2626', mr: 1, fontSize: 16 }} />
                      )}
                      <Typography variant="body2">
                        {t('home.capabilitiesDefined')} ({metrics.totalCapabilities})
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant={canCreateAssessments ? "contained" : "outlined"}
                    color={canCreateAssessments ? cardTones[1].buttonColor : 'primary'}
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleNavigate('/evaluations')}
                    disabled={!canCreateAssessments}
                  >
                    {t('home.createAssessment')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Comparación de Reportes - Solo mostrar si NO es plan free NI trial */}
          {plan !== 'free' && !isTrial && (
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                backgroundColor: canCompareReports ? cardTones[2].cardBg : '#F3F4F6',
                border: canCompareReports ? `1px solid ${cardTones[2].border}` : '1px solid #E5E7EB',
                borderRadius: 2
              }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{
                      backgroundColor: canCompareReports ? cardTones[2].accent : '#9CA3AF',
                      mr: 2,
                      width: 48,
                      height: 48
                    }}>
                      <CompareIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('home.reportComparisonCandidates')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('home.reportComparisonDesc')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2, flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t('home.prerequisites')}:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {metrics.totalReports >= 2 ? (
                        <CheckCircleIcon sx={{ color: '#059669', mr: 1, fontSize: 16 }} />
                      ) : (
                        <WarningIcon sx={{ color: '#DC2626', mr: 1, fontSize: 16 }} />
                      )}
                      <Typography variant="body2">
                        {t('home.twoOrMoreReports')} ({metrics.totalReports})
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant={canCompareReports ? "contained" : "outlined"}
                    color={canCompareReports ? cardTones[2].buttonColor : 'primary'}
                    fullWidth
                    endIcon={<CompareIcon />}
                    onClick={() => handleNavigate('/report_comparison')}
                    disabled={!canCompareReports}
                  >
                    {t('home.compareReports')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Configuración y Gestión */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{
            width: 4,
            height: 40,
            backgroundColor: '#64748B',
            borderRadius: 1,
            mr: 2
          }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('home.configAndManagement')}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Configuración - Siempre mostrar */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              height: '100%',
              backgroundColor: cardTones[0].cardBg,
              border: `1px solid ${cardTones[0].border}`,
              borderRadius: 2
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{
                    backgroundColor: cardTones[0].accent,
                    mr: 2,
                    width: 48,
                    height: 48
                  }}>
                    <WorkIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('home.configuration')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('home.configurationDesc')}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  color={cardTones[0].buttonColor}
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => handleNavigate('/configuration')}
                >
                  {t('home.goToConfiguration')}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Dashboard - Solo mostrar si NO es plan free NI trial */}
          {plan !== 'free' && !isTrial && (
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                backgroundColor: cardTones[1].cardBg,
                border: `1px solid ${cardTones[1].border}`,
                borderRadius: 2
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{
                      backgroundColor: cardTones[1].accent,
                      mr: 2,
                      width: 48,
                      height: 48
                    }}>
                      <BarChartIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('home.dashboard')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('home.dashboardDesc')}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    color={cardTones[1].buttonColor}
                    fullWidth
                    endIcon={<TrendingUpIcon />}
                    onClick={() => handleNavigate('/dashboard')}
                  >
                    {t('home.goToDashboard')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default HomeLayout;
